<template>
  <base-modal
    ref="core"
    :nested="false"
  >
    <template #header>
      <h4>{{ $t('components.sentGeo.modals.addMonitoredTransport') }}</h4>
    </template>

    <template #body>
      <div
        class="form-horizontal form-sent-geo"
        role="form"
      >
        <div class="form-body">
          <div class="form-group">
            <div class="row">
              <div class="col-md-12">
                <label class="control-label">{{ $t('components.sentGeo.modals.dateRange') }}</label>
                <date-picker
                  v-model="dateRange"
                  range
                  :class="{ 'custom-error': v.sentGeo.start.$error }"
                  style="flex-basis: 50%;"
                  @emitDate="emitDate"
                />
                <div
                  v-for="error of v.sentGeo.start.$errors"
                  :key="error.$uid"
                  class="vuelidate-errors"
                >
                  <p class="error-msg">
                    {{ error.$message }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-md-12">
                <label class="control-label">{{ $t('components.sentGeo.modals.vehicle') }}</label>
                <vue-select
                  v-model="v.sentGeo.imei.$model"
                  :class="{ 'custom-error': v.sentGeo.imei.$error, 'is-invalid': v.sentGeo.imei.$error }"
                  :options="vehicles"
                  class="flex-grow vue-select"
                  icon="bi bi-truck"
                  label="label"
                  @updateSelect="emitVehicle"
                />
                <div
                  v-for="error of v.sentGeo.imei.$errors"
                  :key="error.$uid"
                  class="vuelidate-errors"
                >
                  <p class="error-msg">
                    {{ error.$message }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-md-12">
                <label class="control-label">{{ $t('components.sentGeo.modals.referenceNumber') }}</label>
                <input
                  v-model="v.sentGeo.reference_number.$model"
                  :class="{ 'custom-error': v.sentGeo.reference_number.$error }"
                  class="form-control input-sm"
                  placeholder="prefix SENT"
                  type="text"
                >
                <div
                  v-for="error of v.sentGeo.reference_number.$errors"
                  :key="error.$uid"
                  class="vuelidate-errors"
                >
                  <p class="error-msg">
                    {{ error.$message }}
                  </p>
                </div>

                <p
                  v-if="checkFormFilteredDataVar"
                  class="error-msg"
                >
                  {{ $t('components.sentGeo.modals.errorTimeLimitForVehicle') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="pull-right">
        <button
          class="btn btn-default"
          @click="closeModal"
        >
          <i class="fa fa-remove" />
          {{ $t('components.sentGeo.modals.exit') }}
        </button>
        <button
          class="btn btn-primary"
          @click="addSentGeo"
        >
          <i class="fa fa-save" />
          {{ $t('components.sentGeo.modals.save') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import BaseModal from './core/BaseModal.vue'

import { defineComponent } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@/vuelidate/i18n-validators'
import DatePicker from '@/components/extras/DatePicker.vue'
import { datePrettier } from '@/utils/tools'
import { getCompanyFromJwt, getMyVehicles } from '@/crud/getters'
import VueSelect from '@/components/extras/VueSelect.vue'
import { axiosInstance } from '@/http-common'
import { useI18n } from 'vue-i18n'

function sentGeo() {
  return {
    imei: '',
    company_id: '',
    start: '',
    stop: '',
    reference_number: ''
  }
}

export default defineComponent({
  name: 'SentGeoModal',
  components: {
    BaseModal,
    DatePicker,
    VueSelect
  },
  data(): {
    vehicles: Array<any>,
    sentGeo: any,
    checkFormFilteredDataVar: boolean,
    dateRange: [Date?, Date?]
  } {
    return {
      vehicles: [],
      sentGeo: sentGeo(),
      checkFormFilteredDataVar: false,
      dateRange: [] as [Date?, Date?]
    }
  },
  setup() {
    const { t } = useI18n()
    return {
      v: useVuelidate(),
      t
    }
  },
  props: {
    store: {
      type: Array<any>,
      default: []
    }
  },
  validations: {
    sentGeo: {
      imei: { required },
      start: { required },
      stop: { required },
      reference_number: { required }
    }
  },
  watch: {
    sentGeo: {
      handler() {
        this.checkFormFilteredDataVar = this.checkFormFilteredData()
      },
      deep: true
    }
  },
  methods: {
    checkFormFilteredData() {
      const find = this.store.filter((item) => {
        const imeiMatch = this.sentGeo.imei ? item.imei === this.sentGeo.imei : false;

        const startSentGeo = new Date(this.sentGeo.start);
        const stopSentGeo = new Date(this.sentGeo.stop);
        const startItem = new Date(item.start);
        const stopItem = new Date(item.stop);

        const timeMatch = (startSentGeo >= startItem && startSentGeo <= stopItem) ||
            (stopSentGeo >= startItem && stopSentGeo <= stopItem) ||
            (startSentGeo <= startItem && stopSentGeo >= stopItem);

        return imeiMatch && timeMatch;
      });

      return find.length > 0
    },
    openAddModal() {
      const self: any = this
      this.v.$reset()
      self.sentGeo.start = null
      self.sentGeo.stop = null
      self.sentGeo.reference_number = null
      self.$refs.core.openModal()
    },
    closeModal() {
      const self: any = this
      self.$refs.core.closeModal()
    },
    addSentGeo() {
      this.v.$touch()

      if (!this.v.$invalid && !this.checkFormFilteredDataVar) {
        const self: any = this

        this.sentGeo.company_id = getCompanyFromJwt()
        axiosInstance.post('/api/v1/sentgeo', this.sentGeo)
            .then(() => {
              self.$emit('emittedRefreshGrid', true)
              self.$toast.success(this.t('components.sentGeo.modals.successfullyAdded'))
            })
            .catch(() => {
              self.$toast.error(this.t('components.sentGeo.modals.error'))
            })
        self.$refs.core.closeModal()
      }
    },
    datePrettier: datePrettier,
    emitDate(val): void {
      if (Array.isArray(val) && val[0] && val[1]) {
        this.sentGeo.start = this.datePrettier(new Date(val[0]))
        this.sentGeo.stop  = this.datePrettier(new Date(val[1]))
      } else {
        this.sentGeo.start = ''
        this.sentGeo.stop  = ''
      }
    },
    emitVehicle(value): void {
      if (value)
        this.sentGeo.imei = value.device.imei
      else
        this.sentGeo.imei = null
    },
    getVehicleName(obj): string {
      return obj.vars.name == null || obj.vars.name === '' ? obj.registration : `${ obj.vars.name } [${ obj.registration }]`
    }
  },
  mounted() {
    const self = this
    getMyVehicles()
        .then((response: any) => {
          while (this.vehicles.length > 0) {
            this.vehicles.pop()
          }
          response.data.forEach(function (obj) {
            obj.label = self.getVehicleName(obj)
            self.vehicles.push(obj)
          })
        })
  }
})
</script>

<style lang="scss" scoped>
label {
  display: block !important;
}
</style>